import clsx from 'clsx'
import Image from 'next/legacy/image'
import { getDailyAmount } from '@/v2/cars/Helper'
import { useCallback } from 'react'
import Button from '@/components/ui/button'

// Assets
import AC from '@/assets/img/cars/svg/ac.svg'
import Doors from '@/assets/img/cars/svg/doors.svg'
import Gear from '@/assets/img/cars/svg/gear.svg'
import Mileage from '@/assets/img/cars/svg/mileage.svg'
import Pax from '@/assets/img/cars/svg/pax.svg'
import useCurrency from '@/hooks/currency'

const CONSTRUCTOR_ACRONYMS = { BMW: 'BMW', VW: 'Volkswagen' }
function formatModel(model) {
  if (!model) {
    return model
  }

  model = model.toLowerCase()
  for (const acronym in CONSTRUCTOR_ACRONYMS) {
    const p = model.substring(0, acronym.length)?.toUpperCase()
    if (p === acronym) {
      model = CONSTRUCTOR_ACRONYMS[acronym] + model.substring(acronym.length)
      break
    }
  }

  return model
}

export function CarModel({ carOffer, className = '' }) {
  return (
    <span className={`capitalize ${className}`}>
      {formatModel(carOffer?.car?.example_model)}
    </span>
  )
}

export function CarRenter({ carOffer, showLogo = true, multiline = false }) {
  const ratings = [
    { threshold: 9, text: 'Excellent' },
    { threshold: 8, text: 'Très bien' },
    { threshold: 7, text: 'Bien' },
  ]
  const rating_threshold = Math.floor(
    parseFloat(carOffer.renter?.bsp_auto_rating || 0)
  )
  const rating_text =
    ratings.find((r) => r.threshold == rating_threshold)?.text || 'Moyen'

  return (
    <div
      className={
        multiline
          ? 'flex flex-wrap justify-between items-start gap-y-5 gap-x-10'
          : 'flex items-center justify-between gap-10'
      }
    >
      {showLogo && (
        <div>
          <div className="relative w-50 h-30">
            <Image
              src={`https://www.bsp-auto.com/img/adm/l${carOffer.renter.id}.gif`}
              alt=""
              layout="fill"
              objectFit="contain"
            />
          </div>
        </div>
      )}

      <div className="text-16 font-bold flex flex-wrap gap-5 justify-end items-baseline">
        <div
          className={clsx(
            'rounded-l-10 rounded-tr-10 rounded-br-0 p-5 text-16 font-bold',
            {
              'bg-green-80 text-white-90': rating_threshold == 9,
              'bg-green-40 text-[#1C692D]': rating_threshold == 8,
              'bg-green-20 text-[#1C692D]': rating_threshold == 7,
              'bg-warning-light': rating_threshold < 7,
            }
          )}
        >
          {carOffer.renter?.bsp_auto_rating}
        </div>
        <span className="whitespace-nowrap">{rating_text}</span>
      </div>
    </div>
  )
}

export function CarTypeAndDriver({ carOffer, short = false }) {
  if (short) {
    return (
      <h4 className="text-dark-60 text-14">
        ou similaire avec {carOffer.license_years} ans de permis
      </h4>
    )
  }

  return (
    <h4 className="text-dark-60 text-14">
      ou similaire. {carOffer.driver_minimal_age} ans minimum avec{' '}
      {carOffer.license_years} ans de permis
    </h4>
  )
}

export function CarCharacteristics({
  carOffer,
  cols = 2,
  className = 'flex-1 grid',
  itemClassName = '',
}) {
  return (
    <div className={clsx(className, `grid-cols-${cols}`)}>
      <div
        className={clsx(
          'flex items-center border-b border-dashed border-gray-20',
          itemClassName
        )}
      >
        <div className="w-35">
          <Doors className="text-dark-60 mr-5" />
        </div>{' '}
        {carOffer.car.nb_doors} portes
      </div>
      <div
        className={clsx(
          'flex items-center border-b border-dashed border-gray-20',
          itemClassName
        )}
      >
        <div className="w-35">
          <AC className="text-dark-60 mr-5" />
        </div>{' '}
        {carOffer.car.ac_type === 'automated' && <>Climatisation</>}
        {carOffer.car.ac_type !== 'automated' && <>Pas de climatisation</>}
      </div>
      <div
        className={clsx(
          'flex items-center border-b border-dashed border-gray-20',
          itemClassName
        )}
      >
        <div className="w-35">
          <Pax className="text-dark-60 mr-5" />
        </div>{' '}
        {carOffer.car.max_passengers} sièges
      </div>
      <div
        className={clsx(
          'flex items-center ',
          cols <= 2 && 'border-b border-dashed border-gray-20',
          itemClassName
        )}
      >
        <div className="w-35">
          <Gear className="text-dark-60 mr-5" />
        </div>{' '}
        {carOffer.car.gearbox === 'automated' ? 'Automatique' : 'Manuelle'}
      </div>
      <div
        className={clsx(
          'flex items-center',
          cols <= 0 && 'border-b border-dashed border-gray-20',
          itemClassName
        )}
      >
        <div className="w-35">
          <Mileage className="text-dark-60 mr-5" />
        </div>{' '}
        {parseInt(carOffer.included_kilometers) === 0 && (
          <>Kilométrage illimité</>
        )}
        {parseInt(carOffer.included_kilometers) > 0 && (
          <>{carOffer.included_kilometers}km inclus</>
        )}
      </div>
    </div>
  )
}

export function CarPrice({ offerRequest, carOffer, onCarSelected }) {
  const onClick = useCallback(() => onCarSelected(carOffer), [carOffer])
  const { formatCurrency } = useCurrency()

  return (
    <>
      <div className="pr-30">
        <div className="text-14 text-noir-60">Prix par jour :</div>
        <div className="font-bold text-noir text-30">
          {formatCurrency(
            getDailyAmount(carOffer, offerRequest),
            carOffer.currency
          )}
        </div>
      </div>
      <div className="flex-1">
        <Button
          className="w-full"
          size="xl"
          onClick={onClick}
          trackingEventName="cars_show_detail"
        >
          En savoir plus
        </Button>
      </div>
    </>
  )
}

export function SmallCarPrice({ offerRequest, carOffer, onCarSelected }) {
  const onClick = useCallback(() => onCarSelected(carOffer), [carOffer])
  const { formatCurrency } = useCurrency()

  return (
    <>
      <div className="pr-30">
        <div className="text-14 text-noir-60">Prix par jour :</div>
        <div className="font-bold text-noir text-30">
          {formatCurrency(
            getDailyAmount(carOffer, offerRequest),
            carOffer.currency
          )}
        </div>
      </div>
      <div className="flex-1">
        <Button
          className="w-full"
          size="sm"
          onClick={onClick}
          trackingEventName="cars_show_detail"
        >
          En savoir plus
        </Button>
      </div>
    </>
  )
}
